import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  clearSubscriptions,
  subscribeToAction,
} from 'streams/utils/streamUtils';
import type { RelationshipsViewProperties } from './types';
import { RelationshipsViewSettingsBar } from './RelationshipsViewSettingsBar';
import { dispatchAction } from '@ardoq/rxbeach';
import { isAllCollapsed, returnNull } from '@ardoq/common-helpers';
import {
  layoutComplete,
  relationshipsViewSetCollapsedGroupIds,
  relationshipsViewZoomToFit,
} from './actions';
import { PersonalSetting, ViewIds } from '@ardoq/api-types';
import { useViewLegendSubscription } from 'views/viewLegend/useViewLegendSubscription';
import { viewSettingsConsts, updateViewSettings } from '@ardoq/view-settings';
import { getZoomScaleExtent } from './util';
import { usePersonalSettingToggle } from 'streams/currentUser/usePersonalSettingToggle';
import { ErrorInfoBox } from '@ardoq/error-info-box';
import { addToPresentation } from 'viewSettings/exportHandlers';
import { SettingsBarAndViewContainer } from 'tabview/SettingsBarAndViewContainer';
import { ViewContainer } from 'tabview/ViewContainer';
import { EmptyState as EmptyStateBase } from './EmptyState';
import { isPresentationMode } from 'env';
import { ViewLegend, viewLegendCommands } from '@ardoq/view-legend';
import { getViewLegendLabels } from 'viewLegend/getViewLegendLabels';
import { recordRenderComplete } from 'tracking/durationTracking/viewPerformance';
import {
  RelationshipsViewCore,
  type RelationshipsViewCoreRef,
} from './RelationshipsViewCore';
import { select } from 'd3';
import { BUTTON_CLICK_ZOOM_FACTOR } from './consts';
import { noop } from 'lodash';
import { ZoomableViewLegendContainer } from 'tabview/relationshipDiagrams/atoms';
import { toActiveGroups } from './sharedControlsConfigs';
import { redrawUntilCanvasImagesLoad } from 'tabview/canvasRendering/canvasResolvedImages';

const VIEW_ID = ViewIds.RELATIONSHIPS_3;

const RelationshipsView = ({
  rootNode,
  links,
  viewSettings,
  viewInstanceId,
  disconnectedChildren,
  errors,
  hasClones,
  noConnectedComponents,
  groups,
  isViewpointMode,
  isViewEmpty,
  performanceTrackingId,
  i18nLocale,
  errorInfoBoxLabels,
  visualStates,
  relationshipsViewCommands,
  viewStatusReporter,
  uiPropsAndCallbacks: { legendProps },
  // only exists in workspace mode
  traversedIncomingReferenceTypes = [],
  traversedOutgoingReferenceTypes = [],
  referenceTypes = [],
  quickPerspectiveReferenceTypeFilters = null,
}: RelationshipsViewProperties) => {
  const coreRef = useRef<RelationshipsViewCoreRef>(null);

  const viewLegendLabels = useMemo(
    () => getViewLegendLabels(i18nLocale),
    [i18nLocale]
  );

  const onLayoutComplete = useCallback(
    (needsFit: boolean) => {
      if (!rootNode) {
        dispatchAction(layoutComplete({ needsFit: false }), viewInstanceId);
        return;
      }

      const canvas = coreRef.current?.getCanvas();
      const zoom = coreRef.current?.getZoom();
      if (canvas && zoom) {
        zoom.scaleExtent(getZoomScaleExtent(canvas, rootNode));
      }

      dispatchAction(layoutComplete({ needsFit }), viewInstanceId);

      if (performanceTrackingId) {
        recordRenderComplete(performanceTrackingId);
      }
    },
    [rootNode, viewInstanceId, performanceTrackingId]
  );

  // Set up action subscriptions
  useEffect(() => {
    const subscriptions = [
      subscribeToAction(
        layoutComplete,
        ({ needsFit }) => {
          const canvas = coreRef.current?.getCanvas();
          const zoom = coreRef.current?.getZoom();
          if (canvas && zoom) {
            zoom.scaleExtent(getZoomScaleExtent(canvas, rootNode));
          }
          if (needsFit) {
            coreRef.current?.fitNode({});
          }
        },
        viewInstanceId
      ),
      subscribeToAction(
        relationshipsViewZoomToFit,
        ({ node, duration }) => {
          coreRef.current?.fitNode({ node: node || undefined, duration });
        },
        viewInstanceId
      ),
      subscribeToAction(
        relationshipsViewSetCollapsedGroupIds,
        ({ collapsedGroupIds: newCollapsedGroupIds }) => {
          if (!rootNode) {
            return;
          }
          if (!newCollapsedGroupIds.length) {
            coreRef.current?.setBranchOpen(rootNode, true);
          } else if (isAllCollapsed(newCollapsedGroupIds)) {
            coreRef.current?.setBranchOpen(rootNode, false);
          }
        },
        viewInstanceId
      ),
    ];

    return () => {
      clearSubscriptions(subscriptions);
    };
  }, [rootNode, viewInstanceId]);

  const exportContainerRef = useRef<HTMLDivElement>(null);
  const [clearedErrors, setClearedErrors] = useState(false);
  const [clearedHasClones, setClearedHasClones] = useState(false);
  const [neverShowAgain, toggleNeverShowAgain] = usePersonalSettingToggle(
    PersonalSetting.NEVER_SHOW_CLONE_INFO_AGAIN_RELATIONSHIPS_3
  );
  const legendHeightOffset = useViewLegendSubscription();
  const activeGroups = toActiveGroups(groups);

  const zoomIn = () => {
    const canvas = coreRef.current?.getCanvas();
    const zoom = coreRef.current?.getZoom();
    if (canvas && zoom) {
      select(canvas)
        .transition()
        .duration(500)
        .call(zoom.scaleBy, BUTTON_CLICK_ZOOM_FACTOR);
    }
  };

  const zoomOut = () => {
    const canvas = coreRef.current?.getCanvas();
    const zoom = coreRef.current?.getZoom();
    if (canvas && zoom) {
      select(canvas)
        .transition()
        .duration(500)
        .call(zoom.scaleBy, BUTTON_CLICK_ZOOM_FACTOR ** -1);
    }
  };

  const onOptimizeLayout = coreRef.current?.getLayout()?.optimizeLayout ?? noop;
  const isLegendActive = viewSettings[viewSettingsConsts.IS_LEGEND_ACTIVE];

  const createExportCanvas = async () => {
    const layout = coreRef.current?.getLayout();
    if (layout) {
      await redrawUntilCanvasImagesLoad(() => layout.redrawImmediate());
    }
    return exportContainerRef.current?.querySelector('canvas') ?? null;
  };

  useEffect(() => {
    const viewContext = {
      getContainer: () => exportContainerRef.current,
      createExportCanvas,
      getGraphComponent: returnNull,
      zoomIn,
      zoomOut,
      optimizeLayout: onOptimizeLayout,
      toggleLegend: () => {
        viewLegendCommands.updateViewLegendSettings({
          viewId: VIEW_ID,
          isActive: !isLegendActive,
        });
      },
      isLegendActive: Boolean(isLegendActive),
      zoomControls: null,
      zoomControlsDisabledState: 0,
      viewInstanceId,
    };
    relationshipsViewCommands.updateViewContext(viewContext);
  });

  return (
    <SettingsBarAndViewContainer
      data-canvas-hit-test={viewInstanceId}
      className={`tab-pane relationships3Tab active`}
      onContextMenu={e => e.preventDefault()}
    >
      {isViewpointMode ? null : (
        <div className="menuContainer">
          <RelationshipsViewSettingsBar
            activeGroups={activeGroups}
            getExportContainer={() => exportContainerRef.current}
            createExportCanvas={createExportCanvas}
            traversedIncomingReferenceTypes={traversedIncomingReferenceTypes}
            traversedOutgoingReferenceTypes={traversedOutgoingReferenceTypes}
            referenceTypes={referenceTypes}
            quickPerspectiveReferenceTypeFilters={
              quickPerspectiveReferenceTypeFilters
            }
            addToPresentation={() => {
              // we want to store the last clicked node view setting in the presentation view settings, but we do not want it to persist in any way. e.g. it should be forgotten when you change the context.
              // so we update the view setting, save the presentation, and immediately clear the view setting.
              dispatchAction(
                updateViewSettings({
                  viewId: ViewIds.RELATIONSHIPS_3,
                  settings: {
                    lastClickedNodeId:
                      coreRef.current?.getLayout()?.getLastClickedNode()?.id ??
                      null,
                  },
                  persistent: false,
                })
              );
              addToPresentation(ViewIds.RELATIONSHIPS_3, isViewpointMode);
              dispatchAction(
                updateViewSettings({
                  viewId: ViewIds.RELATIONSHIPS_3,
                  settings: {
                    lastClickedNodeId: null,
                  },
                  persistent: false,
                })
              );
            }}
            rootNode={rootNode}
            isEmptyView={isViewEmpty}
            isViewpointMode={isViewpointMode}
            viewInstanceId={viewInstanceId}
            viewSettings={viewSettings}
            i18nLocale={i18nLocale}
          />
        </div>
      )}
      {isViewEmpty ? (
        isViewpointMode ? null : (
          <EmptyStateBase
            noConnectedComponents={noConnectedComponents ?? false}
            viewId={ViewIds.RELATIONSHIPS_3}
          />
        )
      ) : (
        <ViewContainer ref={exportContainerRef}>
          <RelationshipsViewCore
            ref={coreRef}
            rootNode={rootNode}
            links={links}
            viewSettings={viewSettings}
            viewInstanceId={viewInstanceId}
            disconnectedChildren={disconnectedChildren}
            noConnectedComponents={noConnectedComponents}
            isViewpointMode={isViewpointMode}
            isViewEmpty={isViewEmpty}
            performanceTrackingId={performanceTrackingId}
            i18nLocale={i18nLocale}
            visualStates={visualStates}
            relationshipsViewCommands={relationshipsViewCommands}
            viewStatusReporter={viewStatusReporter}
            onLayoutComplete={onLayoutComplete}
          />
          <ErrorInfoBox
            errors={clearedErrors ? [] : errors}
            hasClones={!neverShowAgain && !clearedHasClones && hasClones}
            clearHasClones={() => setClearedHasClones(true)}
            clearErrors={() => setClearedErrors(true)}
            isShowNeverAgainSet={neverShowAgain}
            isPresentationMode={isPresentationMode()}
            toggleNeverShowAgain={toggleNeverShowAgain}
            {...errorInfoBoxLabels}
          />
          <ZoomableViewLegendContainer
            heightOffset={legendHeightOffset || 0}
            visible={isLegendActive}
          >
            <ViewLegend
              componentTypes={[]}
              referenceTypes={[]}
              {...legendProps}
              labels={viewLegendLabels}
            />
          </ZoomableViewLegendContainer>
        </ViewContainer>
      )}
    </SettingsBarAndViewContainer>
  );
};

export { RelationshipsView };
